import { ArrowBack, Check, Close } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	MenuItem,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";

import { ToastContent } from "../../../../../shared/components";
import { useVForm, VForm, VTextField } from "../../../../../shared/forms";
import { VMaskTextField } from "../../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../../shared/hooks";
import { ComissionServices } from "../../../../../shared/services/api/comission/ComissionServices";
import { IInstallmentProposal, ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL, globalRemoveMaskReal2 } from "../../../../../shared/utils";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";

interface ModalComissionLaunchProps {
	isOpenModal: boolean;
	isClose: () => void;
	rows: { id: string; clientId: string; clientName?: string };
	isUpdate: () => void;
	usedInstallmentIds?: string[];
}

// ✅ YUP SCHEMA (sem proposalId e status)
const formValidationSchema = yup.object({
	description: yup.string().required("Campo Obrigatório"),
	type: yup.mixed<"discount" | "bonus" | "prize">().oneOf(["discount", "bonus", "prize"], "Tipo inválido").required("Campo Obrigatório"),
	value: yup.string().required("Campo Obrigatório"),
	dueDate: yup.string().required("Campo Obrigatório"),
	installmentId: yup.string().when("type", {
		is: "bonus",
		then: (schema) => schema.required("Campo Obrigatório para Bonificação"),
		otherwise: (schema) => schema.optional(),
	}),
});

type IComissionLaunch = yup.InferType<typeof formValidationSchema> & {
	proposalId?: string;
	status?: string;
};

export const ModalComissionLaunch = ({ isOpenModal, isClose, rows, isUpdate, usedInstallmentIds = [] }: ModalComissionLaunchProps) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);
	const [isFetchingInstallments, setIsFetchingInstallments] = useState(false);
	const [typeSelected, setTypeSelected] = useState<string>("");
	const [installmentsCache, setInstallmentsCache] = useState<Record<string, IInstallmentProposal[]>>({});

	const installments = useMemo(() => installmentsCache[rows.id] || [], [rows.id, installmentsCache]);
	const today = useMemo(() => new Date().toISOString().split("T")[0], []);

	useEffect(() => {
		if (isOpenModal) {
			formRef.current?.setFieldValue("dueDate", today);

			formRef.current?.setData({
				type: "",
				value: "",
				description: "",
				installmentId: "",
			});
			setTypeSelected("");
		}

		return () => {
			if (!isOpenModal) {
				formRef.current?.setFieldValue("dueDate", "");
			}
		};
	}, [isOpenModal, today]);

	// 🔄 Busca parcelas (com cache)
	const fetchInstallments = async () => {
		if (installmentsCache[rows.id]) return;

		setIsFetchingInstallments(true);
		try {
			const resultList = await ProposalService.getStatusInstallmentProposal("Todas", rows.id);
			if (resultList instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={resultList.message} />,
				});
				return;
			}
			setInstallmentsCache((prev) => ({ ...prev, [rows.id]: resultList || [] }));
		} catch (error) {
			const { titleMessage, contentMessage } = returnMessageError(error);
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
			});
		} finally {
			setIsFetchingInstallments(false);
		}
	};

	// 🧠 Quando muda o tipo
	const handleTypeChange = async (newType: string) => {
		setTypeSelected(newType);

		if (newType === "bonus") {
			formRef.current?.setFieldValue("installmentId", "");
			await fetchInstallments();
		} else {
			// Garante que o campo fica vazio no formulário
			formRef.current?.setFieldValue("installmentId", "");
		}
	};

	const handleSave = async (data: IComissionLaunch) => {
		setIsLoading(true);
		formRef.current?.setErrors({});

		try {
			await formValidationSchema.validate(data, { abortEarly: false });

			const numericValue = globalRemoveMaskReal2(data.value);

			// ✅ CORREÇÃO: Força installmentId = null se o tipo não for "bonus".
			const finalInstallmentId = data.type === "bonus" ? data.installmentId || null : null;

			const finalPayload = {
				description: data.description,
				type: data.type,
				value: numericValue,
				dueDate: `${data.dueDate}T03:00:00`,
				clientId: rows.clientId,
				proposalId: rows.id,
				status: "preview",
				installmentId: finalInstallmentId, // Usamos o valor corrigido
			};

			console.log("Payload final para a API:", finalPayload);

			const result = await ComissionServices.addValues(finalPayload);
			if (result instanceof Error) throw result;

			toastHandler.present({
				type: "success",
				messageOrContent: "Comissão lançada com sucesso!",
				position: "top-right",
			});

			formRef.current?.setData({});
			isUpdate();
			isClose();
		} catch (error: any) {
			const validationErrors: Record<string, string> = {};

			if (error instanceof yup.ValidationError) {
				console.error(
					"Erros de validação do Yup (detalhes):",
					error.inner.map((err) => ({
						path: err.path,
						message: err.message,
					}))
				);

				error.inner.forEach((err) => {
					if (err.path) validationErrors[err.path] = err.message;
				});

				formRef.current?.setErrors(validationErrors);
			} else {
				console.error("Erro no salvamento ou na API:", error);
				const { titleMessage, contentMessage } = returnMessageError(error);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={isOpenModal} onClose={isClose} maxWidth="sm" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Lançamento de Comissão
				</Typography>
				<IconButton onClick={isClose}>
					<Close />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 3 }}>
				<VForm ref={formRef} onSubmit={handleSave} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} placeholder="">
					<Grid container spacing={2}>
						{(isLoading || isFetchingInstallments) && (
							<Grid item xs={12}>
								<LinearProgress />
							</Grid>
						)}

						<Grid item xs={12}>
							<VTextField fullWidth label="Descrição" name="description" />
						</Grid>

						<Grid item xs={4}>
							<VTextField fullWidth label="Tipo de Lançamento" name="type" select onChange={(e) => handleTypeChange(e.target.value)}>
								<MenuItem value="discount">Estorno/Desconto</MenuItem>
								<MenuItem value="bonus">Bonus</MenuItem>
								<MenuItem value="prize">Premiação</MenuItem>
							</VTextField>
						</Grid>

						{/* 🔁 Campo dinâmico apenas se for "bonus" */}
						{typeSelected === "bonus" && (
							<Grid item xs={8}>
								<VTextField fullWidth select name="installmentId" label="Vincular à Parcela">
									<MenuItem value="">Selecione...</MenuItem>

									{installments.length === 0 && !isFetchingInstallments && (
										<MenuItem value="" disabled>
											Nenhuma parcela encontrada
										</MenuItem>
									)}
									{installments.length === 0 && isFetchingInstallments && (
										<MenuItem value="" disabled>
											Carregando parcelas...
										</MenuItem>
									)}

									{installments.flatMap((item, index) => {
										const disabled = usedInstallmentIds.includes(item.id);

										const menuItem = (
											<MenuItem key={item.id} value={item.id} disabled={disabled}>
												Parcela {item.number} — {new Date(item.dueDate).toLocaleDateString("pt-BR")} —{" "}
												{globalFormatCurrencyBRL(item.amount)} {disabled && " (já utilizada)"}
											</MenuItem>
										);

										if (index > 0) {
											return [<Divider key={`divider-${item.id}`} sx={{ my: 0.5 }} />, menuItem];
										}

										return [menuItem];
									})}
								</VTextField>
							</Grid>
						)}

						{typeSelected !== "bonus" && <Grid item xs={8} />}

						<Grid item xs={6}>
							<VMaskTextField
								fullWidth
								label="Valor"
								name="value"
								mask="real"
								InputProps={{
									startAdornment: <InputAdornment position="start">R$</InputAdornment>,
								}}
							/>
						</Grid>

						<Grid item xs={6}>
							<VTextField fullWidth label="Vencimento" name="dueDate" type="date" />
						</Grid>
					</Grid>
				</VForm>
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, borderTop: "1px solid", borderColor: "divider" }}>
				<Button variant="outlined" startIcon={<ArrowBack />} onClick={isClose} disabled={isLoading}>
					Voltar
				</Button>
				<Button variant="contained" color="primary" startIcon={<Check />} onClick={() => formRef.current?.submitForm()} disabled={isLoading}>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
